import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { getUserColor, getUserInitials } from 'src/app/features/smart-inbox/utils/user.util';
import { SharedDialogOptions } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { ITeam, ITeamMember } from '../../../../models/settings.models';

const EMPTY_STATE_VALUE = '--';

/** `"YYYY-MM-DD HH:mm:ss"` — the shape `ITeam.last_edited` actually arrives in. */
const LAST_EDITED_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

/**
 * Read-only view of one team, opened by clicking its row. Mirrors
 * `UserDetailDialogComponent`: same shared-dialog chrome, same identity/section
 * layout, same "the parent owns the store, the dialog only emits intent" split.
 */
@Component({
  selector: 'app-team-detail-dialog',
  templateUrl: './team-detail-dialog.component.html',
  styleUrls: ['./team-detail-dialog.component.scss'],
})
export class TeamDetailDialogComponent implements OnChanges {
  @Input() team!: ITeam;
  @Output() editTeam = new EventEmitter<ITeam>();
  @Output() deleteTeam = new EventEmitter<ITeam>();

  readonly getUserInitials = getUserInitials;
  readonly getUserColor = getUserColor;
  readonly emptyStateValue = EMPTY_STATE_VALUE;

  constructor(
    private readonly dialogRef: MatDialogRef<TeamDetailDialogComponent>,
    private readonly translateService: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const changedTeam = changes['team']?.currentValue as ITeam | undefined;
    if (!changedTeam) return;

    // Rebind to a fresh object so the members list refreshes on input updates.
    this.team = { ...changedTeam };
  }

  setTeam(team: ITeam): void {
    this.team = { ...team };
  }

  get dialogOptions(): SharedDialogOptions {
    return {
      size: 'medium',
      customConfirmButton: true,
      hideConfirmButton: true,
      cancelText: this.translateService.instant('common.close'),
      cancelButtonStyle: 'transparent',
    };
  }

  /** The square chip: the team's first two letters, same rule as the table cell. */
  getTeamInitials(): string {
    const name = (this.team?.name ?? '').trim();
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.charAt(1).toLowerCase();
  }

  getMemberCount(): number {
    if (typeof this.team?.member_count === 'number') return this.team.member_count;
    return Array.isArray(this.team?.members) ? this.team.members.length : 0;
  }

  getMembers(): ITeamMember[] {
    return this.team?.members ?? [];
  }

  /**
   * `last_edited` is a pre-formatted `"YYYY-MM-DD HH:mm:ss"` string, not an ISO
   * datetime, so the parts are read straight out of it: no Date, no timezone,
   * no locale. Same rule as `TeamsComponent.formatLastEdited`.
   */
  getLastEdited(): string {
    const value = this.team?.last_edited;
    if (!value) return EMPTY_STATE_VALUE;

    const parts = LAST_EDITED_PATTERN.exec(value.trim());
    if (!parts) return value;

    const [, year, month, day, hours, minutes] = parts;
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  trackByMemberId(_: number, member: ITeamMember): number {
    return member.id;
  }

  close(): void {
    this.dialogRef.close();
  }

  onEditTeam(): void {
    this.editTeam.emit(this.team);
  }

  onDeleteTeam(): void {
    this.deleteTeam.emit(this.team);
  }
}
